import { Listeners } from './utils.js';

class CosBanner extends HTMLElement {
  constructor() {
    super();
    this.video = null;
    this.longformVideo = null;
    this.closeButton = null;
    this.listeners = new Listeners();
    this.settings = JSON.parse(this.getAttribute('data-settings')) || {};    
  }

  connectedCallback() {
    this.listenForScroll();
    this.initLongformVideoButtons();
  }

  disconnectedCallback() {
    this.listeners.removeAll();
    if (this.longformVideo) {
      this.longformVideo.remove();
      this.longformVideo = null;
    }
    if (this.closeButton) {
      this.closeButton.remove();
      this.closeButton = null;
    }
  }

  listenForScroll() {
    if (!this.getVideoSrc()) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].intersectionRatio <= 0) {
        this.pauseVideo();
      } else {
        (!this.video) ? this.initVideo() : this.playVideo();
      }
    }, {
      rootMargin: '20% 80%',
      threshold: .1
    });

    observer.observe(this);
  }
  
  initVideo() {
    this.video = document.createElement('video');
    this.video.muted = true;
    this.video.loop = 'loop';
    this.video.playsinline = true;
    this.video.setAttribute('preload', 'auto');
    this.video.setAttribute('muted', '');
    this.video.setAttribute('loop', '');
    this.video.setAttribute('playsinline', '');
    this.video.src = this.getVideoSrc();

    const videoWrapper = document.createElement('div');
    videoWrapper.classList.add('video-wrapper', 'images-wrap');
    videoWrapper.appendChild(this.video);

    const imagesWrapp = this.querySelector('.images-wrap');
    (imagesWrapp) ? imagesWrapp.after(videoWrapper) : this.querySelector('.banner').prepend(videoWrapper);
    
    this.listenForOrientationChange();
    this.initControls();
    
    this.playVideo(imagesWrapp);
  }

  listenForOrientationChange() {
    if (!screen) return;
    if (!screen.orientation) return;
    if (!this.video) return;

    screen.orientation.addEventListener("change", (event) => {
      const type = event.target.type;
      const angle = event.target.angle;
      const typeForVideo = (type === 'portrait-primary') ? 'mobile' : 'desktop';

      this.video.src = this.getVideoSrc(typeForVideo);
      this.playVideo();
    });
  }

  getVideoSrc(typeForVideo) {
    return this.settings.videos ? this.settings.videos[typeForVideo || window.matchMedia("(orientation: landscape)").matches ? 'desktop' : 'mobile'].src : null;
  }

  playVideo(imagesWrapp) {
    if (!this.video) return;
    this.video.play()
    .then(() => {
      if (imagesWrapp) imagesWrapp.remove();
    })
    .catch((err) => {
      // Video couldn't play, low power play button showing.
      if (imagesWrapp) {
        const image = imagesWrapp.querySelector('img');
        if (image) image.classList.remove('hidden');
      }
      if (!this.playPauseBtn) return;
      this.playPauseBtn.classList.remove('hidden');      
    });
  }

  pauseVideo() {
    if (!this.video) return;
    this.video.pause();
  }

  hasAudio() {
    if (!this.video) return;
    return this.video.mozHasAudio ||
    Boolean(this.video.webkitAudioDecodedByteCount) ||
    Boolean(this.video.audioTracks && this.video.audioTracks.length);
  }

  initControls() {
    if (!this.settings.video_controls.play_pause_btn && !this.settings.video_controls.sound_btn) return; 

    const controls = document.createElement('div');
    controls.classList.add('video-controls', 'flex', 'btn--group', 'gap15', 'align-center', 'justify-center', 'text-center');

    this.initPlayPauseBtn(controls);
    this.initSoundBtn(controls);

    this.video.after(controls);
  }

  initPlayPauseBtn(controls) {
    if (!this.settings.video_controls.play_pause_btn) return;
    
    // this.video.addEventListener('suspend', () => {
    //   console.log('suspend');
    // });

    this.playPauseBtn = document.createElement('button');
    this.playPauseBtn.classList.add('btn--icon', 'hidden');
    this.playPauseBtn.innerHTML = this.settings.video_controls.play_pause_btn[this.video.paused ? 'on' : 'off'];

    this.listeners.add(this.playPauseBtn, 'click', () => {
      if (this.video.paused) {
        this.playVideo();
      } else {
        this.pauseVideo();
      }
    });
    this.listeners.add(this.video, 'play', () => {
      this.playPauseBtn.innerHTML = this.settings.video_controls.play_pause_btn.off;
    });
    this.listeners.add(this.video, 'pause', () => {
      this.playPauseBtn.innerHTML = this.settings.video_controls.play_pause_btn.on;
    });

    controls.appendChild(this.playPauseBtn);
  }

  initSoundBtn(controls) {
    if (!this.settings.video_controls.sound_btn) return;
    // if (!this.hasAudio()) return;
    const button = document.createElement('button');
    button.classList.add('btn--icon');
    button.innerHTML = this.settings.video_controls.sound_btn[this.video.muted ? 'off' : 'on'];

    this.listeners.add(button, 'click', () => {
      if (this.video.muted) {
        this.video.muted = false;
      } else {
        this.video.muted = true;
      }
    });
    this.listeners.add(this.video, 'volumechange', () => {
      button.innerHTML = this.settings.video_controls.sound_btn[this.video.muted ? 'off' : 'on'];
    });

    controls.appendChild(button);
  }

  initLongformVideoButtons() {
    if (!this.settings.longform_videos) return;

    const buttons = this.querySelectorAll('.longform-video-trigger');
    if (!buttons.length) return;

    buttons.forEach(button => {
      const videoId = button.getAttribute('data-video-id');
      if (!videoId || !this.settings.longform_videos[videoId]) return;

      this.listeners.add(button, 'click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.playLongformVideo(videoId);
      });
    });
  }

  playLongformVideo(videoId) {
    const videoData = this.settings.longform_videos[videoId];
    if (!videoData || !videoData.src) return;

    // Create longform video element if it doesn't exist
    if (!this.longformVideo) {
      this.longformVideo = document.createElement('video');
      this.longformVideo.controls = true;
      this.longformVideo.playsinline = true;
      this.longformVideo.classList.add('longform-video');
      this.longformVideo.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        z-index: 1;
      `;
    }

    // Set video source
    this.longformVideo.src = videoData.src;

    // Replace section content with video
    this.replaceSectionWithVideo();
  }

  replaceSectionWithVideo() {
    if (!this.longformVideo) return;

    // Get the banner section
    const banner = this.querySelector('.banner');
    if (!banner) return;

    // Make banner position relative to contain absolute video
    banner.style.position = 'relative';
    
    // Hide existing content by adding overlay class
    const existingContent = banner.querySelector('.banner-content, .images-wrap, .text-wrap');
    if (existingContent) {
      existingContent.style.opacity = '0';
      existingContent.style.pointerEvents = 'none';
    }

    // Add video to banner if not already there
    if (!this.longformVideo.parentElement) {
      banner.appendChild(this.longformVideo);
    }

    // Add close button
    this.addCloseButton();

    // Play video
    this.longformVideo.play().catch(err => {
      console.error('Error playing longform video:', err);
    });
  }

  addCloseButton() {
    if (this.closeButton) return;

    // Use the close button directly from window.custom.icons.close
    const closeButtonHTML = window.custom?.icons?.close || '<button>×</button>';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = closeButtonHTML;
    this.closeButton = tempDiv.firstElementChild;
    
    // Add positioning styles
    this.closeButton.style.cssText += `
      position: absolute !important;
      top: 20px !important;
      right: 20px !important;
      z-index: 1 !important;
    `;

    this.listeners.add(this.closeButton, 'click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeLongformVideo();
    });

    const banner = this.querySelector('.banner');
    if (banner) {
      banner.appendChild(this.closeButton);
    }
  }

  closeLongformVideo() {
    if (!this.longformVideo) return;

    // Show existing content
    const existingContent = this.querySelector('.banner-content, .images-wrap, .text-wrap');
    if (existingContent) {
      existingContent.style.opacity = '';
      existingContent.style.pointerEvents = '';
    }

    // Remove video and close button
    if (this.longformVideo.parentElement) {
      this.longformVideo.remove();
    }
    if (this.closeButton && this.closeButton.parentElement) {
      this.closeButton.remove();
    }

    // Reset banner position
    const banner = this.querySelector('.banner');
    if (banner) {
      banner.style.position = '';
    }

    // Clean up
    this.longformVideo = null;
    this.closeButton = null;
  }
}
if (!customElements.get('cos-banner')) customElements.define('cos-banner', CosBanner);